import { useState, useEffect } from 'react';
import api from '@/utils/api.utils';
import { University } from '@/types';
import { ToastService, showApiError } from '@/utils/toast';

interface UniversityListItem {
  id: number;
  name: string;
  location: string;
  acceptance_rate: number;
  sat_range_min?: number;
  sat_range_max?: number;
}

interface Prompt {
  id: number;
  prompt_text: string;
  word_count: number;
  primary_category_id: number;
  secondary_category_id: number;
  ai_prompt_specific_advice: string;
}

type SupplementStatus = 'OPTIONAL' | 'REQUIRED' | 'HIGHLY_RECOMMENDED';

interface Supplement {
  id: number;
  name: string;
  description?: string;
  min_number_required: number;
  max_number_required: number;
  status: SupplementStatus;
  ai_specific_advice?: string;
  prompts?: Prompt[];
}

interface PromptCategory {
  id: number;
  name: string;
  overview?: string;
}

export const useAdminPanel = () => {
  const [universities, setUniversities] = useState<UniversityListItem[]>([]);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [universitiesLoading, setUniversitiesLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<Partial<University>>({});
  const [supplements, setSupplements] = useState<Supplement[]>([]);
  const [promptCategories, setPromptCategories] = useState<PromptCategory[]>([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalUniversities, setTotalUniversities] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageSize = 20;

  const fetchPromptCategories = async () => {
    try {
      const response = await api.get('/api/v1/prompt-categories/');
      setPromptCategories(response.data);
    } catch (error) {
      setPromptCategories([]);
    }
  };

  const fetchUniversities = async (page: number = 1, search?: string, loadMore: boolean = false) => {
    try {
      if (loadMore) {
        setIsLoadingMore(true);
      } else {
        setUniversitiesLoading(true);
      }
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: 'name',
        order: 'asc'
      });
      
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await api.get(`/api/v1/universities?${params}`);
      const data = response.data;
      
      if (page === 1 || !loadMore) {
        setUniversities(data.items);
      } else {
        setUniversities(prev => [...prev, ...data.items]);
      }
      
      setHasNextPage(data.has_next);
      setTotalUniversities(data.total);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching universities:', error);
    } finally {
      setUniversitiesLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchUniversityDetails = async (universityId: number) => {
    try {
      setDetailsLoading(true);
      
      const universityResponse = await api.get(`/api/v1/universities/${universityId}`, {
        timeout: 10000
      });
      
      setSelectedUniversity(universityResponse.data);
      setFormData(universityResponse.data);
      
      try {
        const supplementsResponse = await api.get(`/api/v1/universities/${universityId}/supplements-and-applications`, {
          timeout: 10000
        });
        const supplements = supplementsResponse.data.supplements || [];        
        const supplementsWithPrompts = await Promise.all(
          supplements.map(async (supplement: any) => {
            try {
              const promptsResponse = await api.get(`/api/v1/prompts/promptable/SUPPLEMENT/${supplement.id}`);
              return {
                ...supplement,
                prompts: promptsResponse.data || []
              };
            } catch (error) {
              return {
                ...supplement,
                prompts: []
              };
            }
          })
        );
        setSupplements(supplementsWithPrompts);
      } catch (supplementsError) {
        setSupplements([]);
        ToastService.showError('Failed to load supplements. University details loaded successfully.');
      }
    } catch (error: any) {
      setSelectedUniversity(null);
      setFormData({});
      setSupplements([]);
      
      if (error?.code === 'ECONNABORTED') {
        ToastService.showError('Request timed out. Please try again or check your connection.');
      } else {
        showApiError(error);
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  const saveChanges = async () => {
    if (!selectedUniversity) return;

    try {
      setSaving(true);
      const response = await api.put(`/api/v1/universities/${selectedUniversity.id}`, formData);
      
      setSelectedUniversity(response.data);      
      setUniversities(prev => 
        prev.map(uni => 
          uni.id === selectedUniversity.id 
            ? { 
                ...uni, 
                name: response.data.name,
                location: response.data.location,
                acceptance_rate: response.data.acceptance_rate,
                sat_range_min: response.data.sat_range_min,
                sat_range_max: response.data.sat_range_max
              }
            : uni
        )
      );
      
      ToastService.showSuccess('University details saved successfully!');
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof University, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isLoadingMore) {
      fetchUniversities(currentPage + 1, searchQuery, true);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleUniversitySelect = (universityId: number) => {
    fetchUniversityDetails(universityId);
  };

  const handleSupplementCreate = async (supplementData: any) => {
    try {
      const supplementPayload = {
        name: supplementData.name || 'New Supplement',
        description: supplementData.description || '',
        min_number_required: supplementData.min_number_required || 1,
        max_number_required: supplementData.max_number_required || 1,
        status: supplementData.status || 'OPTIONAL',
        ai_specific_advice: supplementData.ai_specific_advice || '',
        university_id: selectedUniversity?.id,
        supplement_number: supplements.length + 1
      };

      const supplementResponse = await api.post('/api/v1/supplements/', supplementPayload);      
      if (supplementData.prompts && Array.isArray(supplementData.prompts)) {
        for (const prompt of supplementData.prompts) {
          if (prompt.prompt_text && prompt.prompt_text.trim()) {
            const promptData = {
              prompt_text: prompt.prompt_text.trim(),
              word_count: prompt.word_count || 500,
              primary_category_id: prompt.primary_category_id > 0 ? prompt.primary_category_id : 1,
              secondary_category_id: prompt.secondary_category_id > 0 ? prompt.secondary_category_id : null,
              ai_prompt_specific_advice: prompt.ai_prompt_specific_advice || null,
              promptable_type: 'SUPPLEMENT',
              promptable_id: supplementResponse.data.id
            };

            await api.post('/api/v1/prompts/', promptData);
          }
        }
      }

      if (selectedUniversity) {
        fetchUniversityDetails(selectedUniversity.id);
      }
      ToastService.showSuccess('Supplement created successfully!');
    } catch (error) {
      console.error('Error creating supplement:', error);
      showApiError(error);
    }
  };

  const handleSupplementUpdate = async (updatedSupplement: any) => {
    try {      
      const supplementResponse = await api.put(`/api/v1/supplements/${updatedSupplement.id}`, {
        name: updatedSupplement.name,
        description: updatedSupplement.description,
        min_number_required: updatedSupplement.min_number_required,
        max_number_required: updatedSupplement.max_number_required,
        status: updatedSupplement.status,
        ai_specific_advice: updatedSupplement.ai_specific_advice
      });

      const currentSupplement = supplements.find(sup => sup.id === updatedSupplement.id);
      const existingPrompts = currentSupplement?.prompts || [];
      const newPrompts = updatedSupplement.prompts || [];
      const existingPromptIds = existingPrompts.map((p: Prompt) => p.id);
      const newPromptIds = newPrompts.map((p: any) => p.id).filter((id: any) => id && id < Date.now() - 86400000);
      const promptsToDelete = existingPromptIds.filter((id: number) => !newPromptIds.includes(id));

      for (const promptId of promptsToDelete) {
        try {
          await api.delete(`/api/v1/prompts/${promptId}`);
        } catch (deleteError) {
          console.error(`Failed to delete prompt ${promptId}:`, deleteError);
        }
      }

      if (updatedSupplement.prompts && Array.isArray(updatedSupplement.prompts)) {
        for (const prompt of updatedSupplement.prompts) {
          if (prompt.prompt_text && prompt.prompt_text.trim()) {
            const promptData = {
              prompt_text: prompt.prompt_text.trim(),
              word_count: prompt.word_count || 500,
              primary_category_id: prompt.primary_category_id > 0 ? prompt.primary_category_id : 1,
              secondary_category_id: prompt.secondary_category_id > 0 ? prompt.secondary_category_id : null,
              ai_prompt_specific_advice: prompt.ai_prompt_specific_advice || null,
              promptable_type: 'SUPPLEMENT',
              promptable_id: updatedSupplement.id
            };

            if (prompt.id && prompt.id > Date.now() - 86400000) {
              await api.post('/api/v1/prompts/', promptData);
            } else if (prompt.id) {
              await api.put(`/api/v1/prompts/${prompt.id}`, promptData);
            }
          }
        }
      }
      
      setSupplements(prev => 
        prev.map(sup => 
          sup.id === updatedSupplement.id ? { 
            ...sup, 
            ...supplementResponse.data, 
            prompts: updatedSupplement.prompts?.filter((p: any) => p.prompt_text && p.prompt_text.trim()) || []
          } : sup
        )
      );

      ToastService.showSuccess('Supplement updated successfully!');
      
    } catch (error) {
      console.error('Error updating supplement:', error);
      showApiError(error);
    }
  };

  const handleUniversityCreate = async (universityData: any) => {
    try {
      const response = await api.post('/api/v1/universities/', universityData);
      fetchUniversities(1, searchQuery, false);
      ToastService.showSuccess('University created successfully!');
      return response.data;
    } catch (error) {
      showApiError(error);
      throw error;
    }
  };

  const handleSupplementDelete = async (supplementId: number) => {
    try {
      await api.delete(`/api/v1/supplements/${supplementId}`);      
      setSupplements(prev => prev.filter(sup => sup.id !== supplementId));
      ToastService.showSuccess('Supplement deleted successfully!');
    } catch (error) {
      console.error('Error deleting supplement:', error);
      showApiError(error);
    }
  };

  const handleUniversityDelete = async (universityId: number) => {
    try {
      await api.delete(`/api/v1/universities/${universityId}`);      
      setUniversities(prev => prev.filter(uni => uni.id !== universityId));      
      if (selectedUniversity?.id === universityId) {
        setSelectedUniversity(null);
        setFormData({});
        setSupplements([]);
      }
      
      ToastService.showSuccess('University deleted successfully!');
    } catch (error) {
      console.error('Error deleting university:', error);
      showApiError(error);
    }
  };

  useEffect(() => {
    fetchUniversities();
    fetchPromptCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUniversities(1, searchQuery, false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return {
    // State
    universities,
    selectedUniversity,
    universitiesLoading,
    detailsLoading,
    saving,
    searchQuery,
    formData,
    supplements,
    currentPage,
    hasNextPage,
    totalUniversities,
    isLoadingMore,
    promptCategories,
    
    saveChanges,
    handleInputChange,
    handleLoadMore,
    handleSearchChange,
    handleUniversitySelect,
    handleSupplementCreate,
    handleSupplementUpdate,
    handleSupplementDelete,
    handleUniversityCreate,
    handleUniversityDelete,
  };
};